import {
  ChangeEventHandler,
  Fragment,
  useEffect,
  useRef,
  useState,
} from "react";
import CustomSuggestiveInputProps from "../../services/models/CustomSuggestiveInputProps.model";
import CustomTextInput from "../global/CustomTextInput";
import { optionType } from "./CustomSelect";
import { useOktaAuth } from "@okta/okta-react";
import utils from "../../services/utils";
import Suggestions from "./Suggestions";
import PillBoxCloseIcon from "../../assets/svg/PillBoxCloseIcon";

function PillBox({
  label,
  dangerouslySetLabel,
  onCross,
}: {
  label: string;
  dangerouslySetLabel: boolean;
  onCross: any;
}): JSX.Element {
  return (
    <div className="relative mr-3 mt-2">
      <div className="p-2 border mr-2 rounded-md bg-primary">
        {dangerouslySetLabel ? (
          <p
            className="text-xs text-white"
            dangerouslySetInnerHTML={{ __html: label }}
          />
        ) : (
          <p className="text-xs text-white">{label}</p>
        )}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onCross?.();
        }}
        className="p-1 absolute -top-2 bg-primary -right-0 shadow-sm  border rounded-full hover:border-[rgba(0,0,0,0.3)] hover:border-3"
      >
        <PillBoxCloseIcon stroke="#fff" />
      </button>
    </div>
  );
}

function CustomSuggestiveInput({
  suggestions,
  search,
  values,
  displayWhen,
  multiSelect,
  disabledWhen,
  filterSuggestion,
  parentContainerClass,
  // restrictToDropDownSelection,
  ...rest
}: CustomSuggestiveInputProps) {
  const oktaAuth = useOktaAuth();
  const authState = oktaAuth?.authState;
  const dropDownRef = useRef<any>();
  const [suggestionsVisible, setSuggestionsVisible] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<any>(rest.value ?? "");
  const [searching, setSearching] = useState<boolean>(false);
  const [suggestionsList, setSuggestionsList] =
    useState<optionType[]>(suggestions);
  const callWhen = Object?.keys(values)?.filter?.((key: string) => {
    return Object?.keys(search?.callWhen ?? {}).includes(key);
  });
  const [searchText, setSearchText] = useState<string>("");
  // console.log("CustomSuggestiveInput Render. Name:", rest.name, "Value:", rest.value);
  const value = multiSelect?.fieldName
    ? rest.value
    : (() => {
        // First try to find in suggestions list
        const foundSuggestion = suggestionsList?.find?.((suggestion) => {
          return suggestion.value == rest.value;
        });
        
        if (foundSuggestion) {
          return foundSuggestion.label;
        }
        
        // If not found and this is ICD code field, use icdCodeDetails as fallback
        if (rest.name === "billingInfo.icdCodeId" && values?.icdCodeDetails && rest.value && values.icdCodeDetails.id == rest.value) {
          return `${values.icdCodeDetails.value} ${values.icdCodeDetails.name}`;
        }
        
        // Final fallback to raw value
        return rest.value;
      })();

  const suggestionsText = multiSelect?.fieldName
    ? searchText
    : typeof rest.value === "string"
      ? rest.value
      : "";
  const disabled = !!disabledWhen
    ? utils.displayHandler(disabledWhen, values)
    : rest.disabled;

  useEffect(() => {
    const unsubscribe = window.addEventListener("mousedown", (event) => {
      const condition =
        dropDownRef?.current && !dropDownRef?.current?.contains(event.target);
      if (condition) return setSuggestionsVisible(false);
    });

    return () => unsubscribe;
  }, []);

  useEffect(
    () => {
      if (search?.sideEffect) {
        if (
          callWhen?.map((key: string) => values[key]).some((value) => !!value)
        ) {
          setSearching(true);
          search.sideEffect(
            authState?.accessToken?.accessToken ?? "",
            callWhen
              ?.map((key: string) => values[key])
              .find((value) => !!value),
            (data) => {
              setSuggestionsList(data);
              setSearching(false);
            }
          );
        } else if (
          search?.callWhen === undefined &&
          !!search?.callOnChange === false
        ) {
          search?.sideEffect(
            authState?.accessToken?.accessToken ?? "",
            "",
            setSuggestionsList
          );
        }
      }
    },
    callWhen?.length > 0 ? callWhen.map((key: string) => values[key]) : []
  );

  useEffect(() => {
    if (!!search?.sideEffect == false) {
      if (suggestions?.length > 0) {
        setSuggestionsList(suggestions);
      }
    }
  }, [suggestions?.length]);

  if (!!displayWhen) {
    if (!utils.displayHandler(displayWhen, values ?? {})) {
      return null;
    }
  }

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const { name, value } = e.target;
    setSearching(true);
    setSuggestionsVisible(true);

    search?.sideEffect?.(
      authState?.accessToken?.accessToken ?? "",
      value,
      (data) => {
        setSuggestionsList(data);
        setSearching(false);
      }
    );

    if (e.target.value === "" && search?.manualFieldsClear) {
      setTimeout(() => {
        search.manualFieldsClear && search.manualFieldsClear(rest.setFieldValue, rest.setFieldTouched);
      }, 0);
    }
    rest?.onChange?.(e);
  };

  const handleInput: ChangeEventHandler<HTMLInputElement> = (e) => {
    e.target.value == ""
      ? setSuggestionsVisible(false)
      : setSuggestionsVisible(true);

    if (multiSelect?.fieldName) {
      setSearchText(e.target.value);
    } /* else if (restrictToDropDownSelection) {
      setInputValue(e.target.value);
      if (e.target.value === "") {
        rest?.onChange?.(e);
      }
    } */ else {
      if (e.target.value === "" && search?.manualFieldsClear) {
        setTimeout(() => {
          search.manualFieldsClear && search.manualFieldsClear(rest.setFieldValue, rest.setFieldTouched);
        }, 0);
      }
      rest?.onChange?.(e);
    }
  };

  return (
    <button
      ref={dropDownRef}
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setSuggestionsVisible((prevState) => !prevState);
      }}
      className={`relative block w-full !text-left ${disabled && "cursor-not-allowed"
        } focus:outline-none ${parentContainerClass}`}
      disabled={disabled}
    >
      <CustomTextInput
        {...rest}
        autoComplete="off"
        value={
          multiSelect?.fieldName
            ? searchText
            : /* !!inputValue
            ? inputValue
            : */ value
        }
        // value={searchText ? searchText : value}
        onChange={search?.callOnChange ? handleChange : handleInput}
        suggestionVisible={suggestionsVisible}
        suggestiveInput={
          suggestionsList.filter((option) => {
            if (filterSuggestion) {
              let foundValue = rest?.getFieldMeta?.(filterSuggestion).value;
              return option?.value !== foundValue;
            }
            return true;
          }).length > 0
        }
        searching={searching}
        parentContainerClass={
          multiSelect?.fieldName && !suggestionsVisible ? "mb-0" : ""
        }
        disabled={disabled}
      />

      <Suggestions
        text={suggestionsText}
        values={values}
        setIsVisible={setSuggestionsVisible}
        isVisible={suggestionsVisible}
        suggestions={suggestionsList.filter((option) => {
          if (filterSuggestion) {
            let foundValue = rest?.getFieldMeta?.(filterSuggestion).value;
            return option?.value !== foundValue;
          }
          return true;
        })}
        multiSelect={multiSelect}
        hasError={
          !!rest?.getFieldMeta?.(rest?.name ?? "").error &&
          !!rest?.getFieldMeta?.(rest?.name ?? "").touched
        }
        onOptionClick={(option) => {
          setSuggestionsVisible(false);
          setSearching(false);
          if (search?.manualFieldsChangeOnOptionClick) {
            search?.manualFieldsChangeOnOptionClick?.(
              option,
              rest.setFieldValue,
              rest.setFieldTouched,
              rest.setFieldError
            );
          } else {
            rest?.setFieldValue?.(rest.labelInfo?.inputId ?? "", option.value);
            if (search?.extraFieldsChangeOnOptionClick) {
              search?.extraFieldsChangeOnOptionClick?.(
                option,
                rest.setFieldValue,
                rest.setFieldTouched
              );
            }
            setInputValue(option.label);
          }
        }}
      />

      {multiSelect?.fieldName && Array.isArray(value) && (
        <div className="flex items-center flex-wrap">
          {value.map((item) => {
            const label =
              suggestionsList.find((option) => option.value == item)?.label ??
              item;
            return (
              <PillBox
                key={item}
                label={label}
                onCross={(e: any) =>
                  rest?.setFieldValue?.(
                    rest.labelInfo?.inputId ?? "",
                    value.filter((data) => data !== item)
                  )
                }
                dangerouslySetLabel={false}
              />
            );
          })}
        </div>
      )}
    </button>
  );
}

export default CustomSuggestiveInput;
